import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const DEBUG = true;

function debugLog(...args) {
  if (!DEBUG) return;
  console.log('[cart-auto-gwp]', ...args);
}

class CartAutoGwp extends HTMLElement {
  #isReconciling = false;
  #queued = false;
  #collectionVariantCache = new Map();

  connectedCallback() {
    debugLog('connected', this.id);
    this.#scheduleReconcile();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartUpdate = (event) => {
    debugLog('cart update event', event?.detail);
    if (event?.detail?.data?.source === 'cart-auto-gwp') return;
    this.#scheduleReconcile();
  };

  #scheduleReconcile() {
    if (this.#isReconciling) {
      this.#queued = true;
      return;
    }

    window.requestAnimationFrame(() => this.#reconcile());
  }

  async #reconcile() {
    if (this.#isReconciling) {
      this.#queued = true;
      return;
    }

    const rules = this.#rules;
    debugLog('reconcile start', { rules });
    if (!rules.length) {
      this.#setCartUpdating(false);
      return;
    }

    this.#isReconciling = true;
    this.hidden = true;
    let hasError = false;

    try {
      const cart = await this.#fetchCart();
      const updates = await this.#getVariantUpdates(cart, rules);
      const isAddingFreeProducts = this.#isAddingFreeProducts(cart, updates);
      debugLog('cart state', {
        itemCount: cart.item_count,
        items: cart.items?.map((item) => ({
          key: item.key,
          variantId: item.variant_id,
          productId: item.product_id,
          title: item.product_title || item.title,
          quantity: item.quantity,
          price: item.price,
          finalPrice: item.final_price,
        })),
        updates,
        isAddingFreeProducts,
      });

      if (!Object.keys(updates).length) return;

      if (isAddingFreeProducts) this.#setCartUpdating(true);
      const data = await this.#updateCart(updates);
      const updatedCart = await this.#fetchCart();
      debugLog('cart updated', {
        response: data,
        updatedItemCount: updatedCart.item_count,
        updatedItems: updatedCart.items?.map((item) => ({
          variantId: item.variant_id,
          title: item.product_title || item.title,
          quantity: item.quantity,
        })),
      });

      document.dispatchEvent(
        new CartUpdateEvent(updatedCart, this.id || 'cart-auto-gwp', {
          source: 'cart-auto-gwp',
          itemCount: updatedCart.item_count,
          sections: data.sections,
        })
      );
    } catch (error) {
      hasError = true;
      this.hidden = true;
      console.warn('[cart-auto-gwp] Unable to reconcile gift with purchase rules', error);
    } finally {
      this.#setCartUpdating(false);
      if (hasError) this.hidden = false;
      this.#isReconciling = false;

      if (this.#queued) {
        this.#queued = false;
        this.#scheduleReconcile();
      }
    }
  }

  get #rules() {
    const scripts = Array.from(this.querySelectorAll('script[data-cart-auto-gwp-rule]'));
    if (!scripts.length) return [];

    try {
      const filteredRules = scripts
        .map((script) => JSON.parse(script.textContent))
        .filter((rule) => rule && typeof rule === 'object');
      debugLog('parsed rules', filteredRules);
      return filteredRules;
    } catch (error) {
      console.warn(
        '[cart-auto-gwp] Invalid cart GWP rules JSON',
        error,
        scripts.map((script) => script.textContent)
      );
      return [];
    }
  }

  async #getVariantUpdates(cart, rules) {
    const updates = {};
    const existingQuantities = this.#getVariantQuantities(cart);
    const desiredGiftQuantities = new Map();
    const ruleGiftVariantIds = new Set();
    const sameProductGiftVariantIds = new Set();

    for (const rule of rules) {
      const giftVariantId = Number(rule.giftVariantId);
      if (!giftVariantId) {
        debugLog('skip rule: missing gift variant', rule);
        continue;
      }
      ruleGiftVariantIds.add(giftVariantId);

      if (rule.customerHasRedeemed) {
        debugLog('skip rule: customer has already redeemed', rule);
        continue;
      }

      if (rule.buySourceType === 'product' && Number(rule.buyVariantId) === giftVariantId) {
        sameProductGiftVariantIds.add(giftVariantId);
      }

      const result = rule.conditionType === 'spend'
        ? await this.#getSpendResult(cart, rule)
        : await this.#getQuantityResult(cart, rule);
      debugLog('rule result', { rule, result });

      if (result.giftQuantity > 0) {
        desiredGiftQuantities.set(giftVariantId, (desiredGiftQuantities.get(giftVariantId) || 0) + result.giftQuantity);
      }
    }

    for (const [giftVariantId, desiredGiftQuantity] of desiredGiftQuantities) {
      const currentQuantity = existingQuantities.get(giftVariantId) || 0;
      const targetQuantity = sameProductGiftVariantIds.has(giftVariantId)
        ? currentQuantity + desiredGiftQuantity
        : desiredGiftQuantity;
      debugLog('variant target', {
        giftVariantId,
        currentQuantity,
        desiredGiftQuantity,
        targetQuantity,
      });

      if (targetQuantity !== currentQuantity) {
        updates[giftVariantId] = targetQuantity;
      }

    }

    for (const giftVariantId of ruleGiftVariantIds) {
      if (desiredGiftQuantities.has(giftVariantId) || sameProductGiftVariantIds.has(giftVariantId)) continue;

      const currentQuantity = existingQuantities.get(giftVariantId) || 0;
      if (currentQuantity > 0) updates[giftVariantId] = 0;
    }

    return updates;
  }

  async #getQuantityResult(cart, rule) {
    const qualifyingItems = await this.#getQualifyingItems(cart, rule);
    const qualifyingQuantity = qualifyingItems.reduce((total, item) => {
      return total + (Number(item.quantity) || 0);
    }, 0);
    const buyQuantity = Math.max(Number(rule.buyQuantity) || 1, 1);
    const giftQuantity = Math.max(Number(rule.giftQuantity) || 1, 1);
    const sameProductGift = rule.buySourceType === 'product' && Number(rule.buyVariantId) === Number(rule.giftVariantId);
    debugLog('quantity condition', {
      rule,
      qualifyingItems: qualifyingItems.map((item) => ({
        variantId: item.variant_id,
        title: item.product_title || item.title,
        quantity: item.quantity,
      })),
      qualifyingQuantity,
      buyQuantity,
      giftQuantity,
      sameProductGift,
    });

    if (qualifyingQuantity < buyQuantity) return { paidQuantity: qualifyingQuantity, giftQuantity: 0 };

    if (sameProductGift) {
      const groupQuantity = buyQuantity + giftQuantity;
      const completedGroups = rule.repeatPerMultiple ? Math.floor(qualifyingQuantity / groupQuantity) : 0;
      const remainderQuantity = rule.repeatPerMultiple ? qualifyingQuantity % groupQuantity : qualifyingQuantity;
      const baseQuantity = completedGroups * groupQuantity;
      const targetQuantity = baseQuantity + (
        remainderQuantity >= buyQuantity && remainderQuantity < groupQuantity ? groupQuantity : remainderQuantity
      );

      return {
        paidQuantity: qualifyingQuantity,
        giftQuantity: Math.max(targetQuantity - qualifyingQuantity, 0),
      };
    }

    return {
      paidQuantity: qualifyingQuantity,
      giftQuantity: rule.repeatPerMultiple ? Math.floor(qualifyingQuantity / buyQuantity) * giftQuantity : giftQuantity,
    };
  }

  async #getSpendResult(cart, rule) {
    const qualifyingItems = await this.#getQualifyingItems(cart, rule);
    const giftVariantId = Number(rule.giftVariantId);
    const qualifyingSubtotal = qualifyingItems.reduce((total, item) => {
      if (giftVariantId && Number(item.variant_id) === giftVariantId) {
        return total;
      }

      return total + this.#lineFinalPrice(item);
    }, 0);
    const threshold = Number(rule.threshold) || 0;
    const minimumThreshold = Number(rule.minimumThreshold) || 0;
    const giftQuantity = Math.max(Number(rule.giftQuantity) || 1, 1);
    const maxGiftQuantity = Number(rule.maxGiftQuantity) || 0;
    debugLog('spend condition', {
      rule,
      qualifyingItems: qualifyingItems.map((item) => ({
        variantId: item.variant_id,
        title: item.product_title || item.title,
        quantity: item.quantity,
        finalPrice: item.final_price,
        finalLinePrice: item.final_line_price,
        excludedGift: giftVariantId && Number(item.variant_id) === giftVariantId,
      })),
      qualifyingSubtotal,
      threshold,
      minimumThreshold,
      giftQuantity,
      maxGiftQuantity,
    });

    if (threshold <= 0 || qualifyingSubtotal < threshold || (minimumThreshold > 0 && qualifyingSubtotal < minimumThreshold)) {
      return { paidQuantity: qualifyingSubtotal, giftQuantity: 0 };
    }

    const calculatedGiftQuantity = rule.repeatPerMultiple
      ? Math.floor(qualifyingSubtotal / threshold) * giftQuantity
      : giftQuantity;

    return {
      paidQuantity: qualifyingSubtotal,
      giftQuantity: maxGiftQuantity > 0
        ? Math.min(calculatedGiftQuantity, maxGiftQuantity)
        : calculatedGiftQuantity,
    };
  }

  async #getQualifyingItems(cart, rule) {
    if (rule.buySourceType === 'collection') {
      const handles = Array.isArray(rule.buyCollectionHandles)
        ? rule.buyCollectionHandles.filter(Boolean)
        : [rule.buyCollectionHandle].filter(Boolean);
      const variantIds = await this.#getCollectionVariantIdsForHandles(handles);
      return cart.items.filter((item) => variantIds.has(Number(item.variant_id)));
    }

    const triggerVariantId = Number(rule.buyVariantId);
    if (!triggerVariantId) return [];

    return cart.items.filter((item) => Number(item.variant_id) === triggerVariantId);
  }

  async #updateCart(updates) {
    const body = {
      updates,
    };
    const sectionIds = this.#cartSectionIds;

    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const cartUpdateUrl = window.Theme?.routes?.cart_update_url || '/cart/update.js';
    debugLog('update cart request', { url: cartUpdateUrl, body });
    const response = await fetch(cartUpdateUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();
    debugLog('update cart response', { ok: response.ok, status: response.status, data });
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || data.errors || 'Cart update failed');
    }

    return data;
  }

  async #fetchCart() {
    const cartUrl = window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : '/cart.js';
    debugLog('fetch cart', cartUrl);
    const response = await fetch(cartUrl, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

    const cart = await response.json();
    debugLog('fetch cart response', {
      itemCount: cart.item_count,
    });
    return cart;
  }

  async #getCollectionVariantIds(handle) {
    if (!handle) return new Set();

    if (!this.#collectionVariantCache.has(handle)) {
      this.#collectionVariantCache.set(handle, this.#fetchCollectionVariantIds(handle));
    }

    return this.#collectionVariantCache.get(handle);
  }

  async #getCollectionVariantIdsForHandles(handles) {
    const variantIds = new Set();

    for (const handle of handles) {
      const collectionVariantIds = await this.#getCollectionVariantIds(handle);
      collectionVariantIds.forEach((variantId) => variantIds.add(variantId));
    }

    return variantIds;
  }

  async #fetchCollectionVariantIds(handle) {
    const variantIds = new Set();
    let page = 1;
    let hasMore = true;

    while (hasMore && page <= 10) {
      const response = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`, {
        headers: { Accept: 'application/json' },
        cache: 'force-cache',
      });

      if (!response.ok) throw new Error(`Collection products request failed: ${response.status}`);

      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];

      products.forEach((product) => {
        product.variants?.forEach((variant) => {
          variantIds.add(Number(variant.id));
        });
      });

      hasMore = products.length === 250;
      page += 1;
    }

    return variantIds;
  }

  #getVariantQuantities(cart) {
    const quantities = new Map();

    cart.items.forEach((item) => {
      const variantId = Number(item.variant_id);
      quantities.set(variantId, (quantities.get(variantId) || 0) + Number(item.quantity || 0));
    });

    return quantities;
  }

  #lineFinalPrice(item) {
    const linePrice = Number(item.final_line_price);

    if (Number.isFinite(linePrice)) {
      return linePrice;
    }

    const paidQuantity = Number(item.quantity) || 0;
    const unitPrice = Number(item.final_price ?? item.price ?? 0);

    return paidQuantity * unitPrice;
  }

  #isAddingFreeProducts(cart, updates) {
    const existingQuantities = this.#getVariantQuantities(cart);

    return Object.entries(updates).some(([variantId, targetQuantity]) => {
      const currentQuantity = existingQuantities.get(Number(variantId)) || 0;
      return Number(targetQuantity) > currentQuantity;
    });
  }

  get #cartSectionIds() {
    const sectionIds = new Set();

    document.querySelectorAll('cart-items-component').forEach((element) => {
      const sectionId = element instanceof HTMLElement ? element.dataset.sectionId : '';
      if (sectionId) sectionIds.add(sectionId);
    });

    return Array.from(sectionIds);
  }

  #setCartUpdating(isUpdating) {
    this.hidden = !isUpdating;
    this.classList.toggle('cart-auto-gwp--updating', isUpdating);
    document.body.classList.toggle('cart-auto-gwp-is-updating', isUpdating);

    document.querySelectorAll('.cart-form :is(input, button, select, textarea)').forEach((control) => {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLButtonElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement)) {
        return;
      }

      if (isUpdating) {
        control.dataset.cartAutoGwpWasDisabled = control.disabled ? 'true' : 'false';
        control.disabled = true;
        return;
      }

      if (control.dataset.cartAutoGwpWasDisabled === 'false') {
        control.disabled = false;
      }

      delete control.dataset.cartAutoGwpWasDisabled;
    });
  }

}

if (!customElements.get('cart-auto-gwp')) {
  customElements.define('cart-auto-gwp', CartAutoGwp);
}
